// src/screens/s3/LifestyleBusiness.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Session 3 — Lifestyle Business Scenario
// The business doesn't have to stop completely. What if it ran at a different pace?
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import { ClientData } from '../../types/ClientData';
import { formatCurrency } from '../../utils/format';
import CurrencyInput from '../../components/CurrencyInput';
import ScenarioScreenWrapper from './ScenarioScreenWrapper';

interface Props {
  data: ClientData;
  onUpdate: (updates: Partial<ClientData>) => void;
}

export default function LifestyleBusiness({ data, onUpdate }: Props) {
  const isCommitted = data.s3ScenariosCommitted.includes('lifestyle');
  const monthlyIncome = data.s3LifestyleBusinessMonthlyIncome || 0;
  const potReduction = monthlyIncome * 240; // 20-year annuity factor

  const voyantNote = `Your lifestyle business generates £${monthlyIncome}/month, reducing the required portfolio by approximately ${formatCurrency(potReduction)}.`;

  const handleCommit = () => {
    const updated = data.s3ScenariosCommitted.includes('lifestyle')
      ? data.s3ScenariosCommitted
      : [...data.s3ScenariosCommitted, 'lifestyle'];
    onUpdate({ s3ScenariosCommitted: updated });
  };

  return (
    <ScenarioScreenWrapper
      title="Lifestyle Business"
      question="You built this business. It doesn't have to stop completely. What if it just ran at a different pace — the parts you love, on your terms?"
      clientName={data.clientName}
      voyantNote={voyantNote}
      isCommitted={isCommitted}
      onCommit={handleCommit}
      voyantReadbacks={
        <div className="space-y-4">
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-1">
              Portfolio Reduction
            </p>
            <p className="text-2xl font-bold text-ma-navy">
              {formatCurrency(data.s3LifestylePotReduction || potReduction)}
            </p>
            <p className="text-xs text-gray-600 mt-2">
              Every £{monthlyIncome}/month reduces your required pot by £{potReduction.toLocaleString()}
            </p>
          </div>
        </div>
      }
    >
      <div className="space-y-6">
        <div>
          <CurrencyInput
            label="Monthly Income from Lifestyle Business"
            value={monthlyIncome}
            onChange={(v) => onUpdate({ s3LifestyleBusinessMonthlyIncome: v })}
            note="What ongoing income would you keep from the business?"
          />
        </div>

        <div>
          <label className="text-xs text-gray-400 uppercase tracking-wide font-medium block mb-3">
            What Happens to the Business at Freedom?
          </label>
          <div className="space-y-2">
            {[
              { value: 'sold', label: 'Sold — completely exit' },
              { value: 'wound-down', label: 'Wound down — taper to zero' },
              { value: 'lifestyle', label: 'Lifestyle — ongoing part-time income' },
              { value: 'handed-over', label: 'Handed over — gift to next generation' },
            ].map((option) => (
              <label
                key={option.value}
                className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition"
              >
                <input
                  type="radio"
                  name="businessAtFreedom"
                  value={option.value}
                  checked={data.s3BusinessAtFreedom === option.value}
                  onChange={(e) => onUpdate({ s3BusinessAtFreedom: e.target.value as any })}
                  className="w-4 h-4 text-ma-navy focus:ring-ma-navy"
                />
                <span className="ml-3 text-sm text-gray-700">{option.label}</span>
              </label>
            ))}
          </div>
        </div>
      </div>
    </ScenarioScreenWrapper>
  );
}
